import React from 'react'
import { createPortal } from 'react-dom'
import { ALLIANCE_TASK_TITLE_STYLE } from '../../common/allianceTaskOptionsClient'

/**
 * Shared parchment styling for the leader's "set the alliance task" sub-flow modals
 * (APS target, Diplomacy level, Technology). Matches the Conquer/Raid attack-plan modal:
 * a stretched `modal-bg` frame sized in container-query units (`cqw`), the gold antique
 * title, dark `text-bg` option cards, and the `confirm-btn` plate.
 */

const AT = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task'
export const TASK_MODAL_BG = `${AT}/modal-bg.webp`
export const TASK_TEXT_BG = `${AT}/text-bg.webp`
export const TASK_CONFIRM_BTN = `${AT}/confirm-btn.webp`
export const TASK_BACK_BTN = `${AT}/back-btn.webp`
export const TASK_TITLE_BG = `${AT}/title-bg.webp`
export const TASK_CINZEL = '"Cinzel", "Trajan Pro", "Times New Roman", serif'

/** Small uppercase antique-gold field label (readable on parchment). */
export const taskLabelStyle: React.CSSProperties = {
  fontFamily: TASK_CINZEL,
  fontWeight: 700,
  fontSize: '2.4cqw',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#5a3c17',
}

/** Dark parchment field (select / number input) with a gold border, matching the frame. */
export const taskFieldStyle: React.CSSProperties = {
  height: '10cqw',
  width: '100%',
  padding: '0 3cqw',
  background: 'rgba(12,8,4,0.72)',
  border: '0.4cqw solid rgba(201,162,75,0.55)',
  borderRadius: '1.6cqw',
  color: '#f3ead0',
  fontFamily: TASK_CINZEL,
  fontWeight: 700,
  fontSize: '2.9cqw',
  outline: 'none',
}

export const TaskModalShell: React.FC<{
  title: string
  subtitle?: string
  width?: string
  padding?: string
  children: React.ReactNode
}> = ({ title, subtitle, width = 'min(92vw, 520px)', padding = '7.5% 7% 5.5%', children }) => {
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      className="fixed inset-0 z-[240] flex items-start justify-center overflow-y-auto bg-black/80 p-4"
      style={{ zIndex: 240 }}
      role="presentation"
    >
      <div
        className="relative my-auto w-full select-none"
        style={{
          width,
          containerType: 'inline-size',
          backgroundImage: `url(${TASK_MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding,
          filter: 'drop-shadow(0 18px 52px rgba(0,0,0,0.7))',
        }}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6.4cqw' }}>
          {title}
        </h2>
        {subtitle ? (
          <p
            className="text-center"
            style={{ fontFamily: TASK_CINZEL, fontWeight: 600, fontSize: '2.7cqw', color: '#6a4d2c', margin: '1.6cqw 0 0', letterSpacing: '0.02em' }}
          >
            {subtitle}
          </p>
        ) : null}
        <div style={{ marginTop: '5cqw' }}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}

/** A selectable option row (dark text-bg card, gold check + governance hint when picked). */
export const TaskOptionCard: React.FC<{
  selected: boolean
  disabled?: boolean
  onClick: () => void
  label: string
  hint?: string
}> = ({ selected, disabled, onClick, label, hint }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="relative flex w-full items-center justify-between transition-[filter,transform] hover:brightness-[1.06] active:scale-[0.99] disabled:cursor-not-allowed"
    style={{
      height: '13cqw',
      backgroundImage: `url(${TASK_TEXT_BG})`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      padding: '0 5cqw',
      filter: selected ? 'brightness(1.08) drop-shadow(0 0 8px rgba(240,212,136,0.85))' : 'brightness(0.9)',
    }}
  >
    <span style={{ fontFamily: TASK_CINZEL, fontWeight: 800, fontSize: '4cqw', color: '#f3ead0' }}>{label}</span>
    <span className="flex items-center" style={{ gap: '2.5cqw' }}>
      {hint ? (
        <span style={{ fontFamily: TASK_CINZEL, fontWeight: 700, fontSize: '2.8cqw', color: '#8fe0aa' }}>{hint}</span>
      ) : null}
      {selected ? <span style={{ color: '#ffe6a0', fontSize: '4.6cqw', fontWeight: 900 }}>✓</span> : null}
    </span>
  </button>
)

/** The gold confirm plate (confirm-btn art). */
export const TaskConfirmButton: React.FC<{
  onClick: () => void
  disabled?: boolean
  label: string
}> = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
    style={{
      marginTop: '6cqw',
      height: '12cqw',
      backgroundImage: `url(${TASK_CONFIRM_BTN})`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      fontFamily: TASK_CINZEL,
      fontWeight: 800,
      fontSize: '3.4cqw',
      letterSpacing: '0.04em',
      color: '#fff2c8',
      textShadow: '0 1px 2px rgba(30,15,4,0.9)',
    }}
  >
    {label}
  </button>
)

/** Footer with a Back plate (back-btn) + Confirm plate (confirm-btn), matching the attack-plan modal. */
export const TaskFooter: React.FC<{
  onBack: () => void
  backLabel: string
  onConfirm: () => void
  confirmLabel: string
  confirmDisabled?: boolean
  backDisabled?: boolean
}> = ({ onBack, backLabel, onConfirm, confirmLabel, confirmDisabled, backDisabled }) => (
  <div className="flex items-stretch" style={{ marginTop: '6cqw', gap: '3cqw' }}>
    <button
      type="button"
      onClick={onBack}
      disabled={backDisabled}
      className="uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:opacity-50"
      style={{
        width: '32%',
        height: '12cqw',
        backgroundImage: `url(${TASK_BACK_BTN})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        fontFamily: TASK_CINZEL,
        fontWeight: 800,
        fontSize: '3.2cqw',
        letterSpacing: '0.04em',
        color: '#f3ead0',
        textShadow: '0 1px 2px rgba(30,15,4,0.85)',
      }}
    >
      {backLabel}
    </button>
    <button
      type="button"
      onClick={onConfirm}
      disabled={confirmDisabled}
      className="flex-1 uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        height: '12cqw',
        backgroundImage: `url(${TASK_CONFIRM_BTN})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        fontFamily: TASK_CINZEL,
        fontWeight: 800,
        fontSize: '3.4cqw',
        letterSpacing: '0.04em',
        color: '#fff2c8',
        textShadow: '0 1px 2px rgba(30,15,4,0.9)',
      }}
    >
      {confirmLabel}
    </button>
  </div>
)
